import { Button, Grid } from '@material-ui/core'
import { useContext, useEffect, useState } from 'react'
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import {
  deleteAddress,
  listUserAddresses,
} from '../../../actions/addressActions'
import { updateProject } from '../../../actions/projectActions'
import { updateUserInfo } from '../../../actions/userActions'
import {
  ADDRESS_CREATE_RESET,
  ADDRESS_DELETE_RESET,
  ADDRESS_UPDATE_RESET,
} from '../../../constants/addressConstants'
import {
  PROJECT_CURRENT_STEP,
  PROJECT_EXIT_REQUEST,
  PROJECT_UPDATE_RESET,
} from '../../../constants/projectConstants'
import { useInput } from '../../../hooks/useInput'
import { Context } from '../../../providers/ProjectContextProvider'
import AddEditAddress from '../../Address/AddEditAddress'
import AddressBookTable from '../../Address/AddressBookTable'
import FormContainer from '../../Form/FormContainer'
import SelectInput from '../../Form/SelectInput'
import TextAreaInput from '../../Form/TextAreaInput'
import TextInput from '../../Form/TextInput'
import Loader from '../../Loader/Loader'
import ModalPopUp from '../../ModalPopUp/ModalPopUp'

interface DeliveryDetailsProps {
  projectCreateContext?: boolean
  handleCancel?: () => void
}

const DeliveryDetails = ({
  projectCreateContext = true,
  handleCancel,
}: DeliveryDetailsProps) => {
  const dispatch = useDispatch()
  const history = useHistory()

  const { setShowNotifyUser } = useContext(Context)

  const { userInfo } = useSelector((state: RootStateOrAny) => state.userLogin)

  const [saveAndExit, setSaveAndExit] = useState(false)
  const {
    value: carrier,
    bind: bindCarrier,
    setValue: setCarrier,
  } = useInput(userInfo.carrier ? userInfo.carrier : '')
  const {
    value: accountNumber,
    bind: bindAccountNumber,
    setValue: setAccountNumber,
  } = useInput(userInfo.accountNumber ? userInfo.accountNumber : '')

  const {
    value: shippingMethod,
    bind: bindShippingMethod,
    setValue: setShippingMethod,
  } = useInput('')
  const {
    value: additionalComments,
    bind: bindAdditionalComments,
    setValue: setAdditionalComments,
  } = useInput('')
  const {
    value: expedited,
    bind: bindExpedited,
    setValue: setExpedited,
  } = useInput('')
  const [addressesSet, setAddressesSet] = useState(false)
  const [showAddressModal, setShowAddressModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedAddress, setSelectedAddress] = useState<Address | undefined>(
    undefined
  )
  const [shippingAddress, setShippingAddress] = useState('')
  const [returnAddress, setReturnAddress] = useState('')
  const [detailsAreSet, setDetailsAreSet] = useState(false)
  const [stayOnPage, setStayOnPage] = useState(false)

  const { project } = useSelector(
    (state: RootStateOrAny) => state.projectDetails
  )
  const { success: addressListSuccess, addressBook } = useSelector(
    (state: RootStateOrAny) => state.addressList
  )
  const { success } = useSelector(
    (state: RootStateOrAny) => state.addressCreate
  )
  const { success: addressUpdateSuccess } = useSelector(
    (state: RootStateOrAny) => state.addressUpdate
  )
  const { loading: loadingDelete, success: successDelete } = useSelector(
    (state: RootStateOrAny) => state.addressDelete
  )
  const { loading: loadingUpdate, success: successProjectUpdate } = useSelector(
    (state: RootStateOrAny) => state.projectUpdate
  )

  // If the user is an admin, then they should use the project owner's address book
  // Otherwise, use the current ID so contributors can also use this component
  const userIDForAddressBook = userInfo.isAdmin ? project.user : userInfo._id

  useEffect(() => {
    if (!userInfo) {
      history.push(`/login?redirect=${window.location.pathname}`)
    }

    if (project.deliveryDetails && !detailsAreSet) {
      if (Object.keys(project.deliveryDetails).length !== 0) {
        setDetailsAreSet(true)
        setShippingAddress(project.deliveryDetails.shippingAddress)
        setReturnAddress(project.deliveryDetails.returnAddress)
        setCarrier(project.deliveryDetails.carrier)
        setAccountNumber(project.deliveryDetails.accountNumber)
        setAdditionalComments(project.deliveryDetails.additionalComments)
        setShippingMethod(project.deliveryDetails.shippingMethod)
        setExpedited(project.deliveryDetails.expedited ? 'Yes' : 'No')
      }
    }

    if (stayOnPage) {
      setStayOnPage(false)
    } else {
      if (successProjectUpdate) {
        dispatch({ type: PROJECT_UPDATE_RESET })

        if (!projectCreateContext) {
          handleCancel?.()
        }

        if (saveAndExit) {
          dispatch({ type: PROJECT_EXIT_REQUEST })
        } else {
          dispatch({
            type: PROJECT_CURRENT_STEP,
            payload: 8,
          })
        }
      }
    }

    if (!addressesSet) {
      dispatch(listUserAddresses(userIDForAddressBook))
      setAddressesSet(true)
    }

    if (success) {
      dispatch({ type: ADDRESS_CREATE_RESET })
      dispatch(listUserAddresses(userIDForAddressBook))
      setShowAddressModal(false)
    }

    if (addressUpdateSuccess) {
      dispatch({ type: ADDRESS_UPDATE_RESET })
      dispatch(listUserAddresses(userIDForAddressBook))
      setShowAddressModal(false)
    }

    if (successDelete) {
      setSelectedAddress(undefined)
      dispatch({ type: ADDRESS_DELETE_RESET })
      dispatch(listUserAddresses(userIDForAddressBook))
      setShowDeleteModal(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    addressBook,
    addressesSet,
    success,
    successDelete,
    addressUpdateSuccess,
    addressListSuccess,
    returnAddress,
    shippingAddress,
    detailsAreSet,
    successProjectUpdate,
    project,
    setCarrier,
    setAccountNumber,
    setShippingAddress,
    setAdditionalComments,
    setExpedited,
    setShippingMethod,
    saveAndExit,
    history,
    userInfo,
  ])

  const handleNext = () => {
    let projectClone = JSON.parse(JSON.stringify(project)) // deep cloning

    let updatedUser = userInfo

    projectClone.deliveryDetails = {
      carrier,
      accountNumber,
      shippingMethod,
      expedited: expedited === 'Yes' ? true : false,
      additionalComments,
    }

    //Avoid bug where can't save empty string to ObjectId field
    if (shippingAddress) {
      projectClone.deliveryDetails.shippingAddress = shippingAddress
    }

    //Avoid bug where can't save empty string to ObjectId field
    if (returnAddress) {
      projectClone.deliveryDetails.returnAddress = returnAddress
    }

    //update user info to be latest used carrier and account number value
    if ((userInfo.isAdmin && carrier) || accountNumber) {
      updatedUser.carrier = carrier
      updatedUser.accountNumber = accountNumber

      dispatch(updateUserInfo(updatedUser))
    }

    dispatch(updateProject(projectClone))
  }

  const deleteHandler = (id: string) => {
    dispatch(deleteAddress(id))
  }

  const handleDeleteClose = () => {
    setSelectedAddress(undefined)
    setShowDeleteModal(false)
  }

  const handleSaveAndExit = () => {
    if (userInfo.isAdmin) {
      setStayOnPage(true)
      setShowNotifyUser(true)
      handleNext()
    } else {
      setSaveAndExit(true)
      handleNext()
    }
  }

  return (
    <div className="delivery-details">
      <FormContainer>
        {projectCreateContext && (
          <>
            <div className="flex space-between">
              <h4 className="m-b-30">
                Delivery details{' '}
                {userInfo.isAdmin &&
                  project.userName &&
                  `for ${project.userName}`}
              </h4>
            </div>

            <p>
              Please specify what address and services should be used for return
              shipping.
            </p>
          </>
        )}
        <AddressBookTable
          setShowModal={setShowAddressModal}
          setShowDeleteModal={setShowDeleteModal}
          setSelectedAddress={setSelectedAddress}
          addressId={shippingAddress}
          setAddressId={setShippingAddress}
          type="Shipping"
        />

        <AddressBookTable
          setShowModal={setShowAddressModal}
          setShowDeleteModal={setShowDeleteModal}
          setSelectedAddress={setSelectedAddress}
          addressId={returnAddress}
          setAddressId={setReturnAddress}
          type="Return"
        />

        <Grid container spacing={4} className="p-b-0">
          <Grid item xs={6}>
            <TextInput name="carrie" label="Carrier" bindValue={bindCarrier} />
          </Grid>

          <Grid item xs={6} className="p-b-0">
            <TextInput
              name="accountNumber"
              label="Carrier account number"
              bindValue={bindAccountNumber}
            />
          </Grid>

          <Grid item xs={6} className="p-t-0">
            <SelectInput
              label="Shipping method"
              name="shippingMethod"
              options={[
                { value: 'Ground', label: 'Ground' },
                { value: 'Freight', label: 'Freight' },
                { value: 'Air', label: 'Air' },
              ]}
              bindValue={bindShippingMethod}
            />
          </Grid>

          <Grid item xs={6} className="p-t-0">
            <SelectInput
              label="Does return shipping need to be expedited?"
              name="expedited"
              options={[
                { value: 'Yes', label: 'Yes' },
                { value: 'No', label: 'No' },
              ]}
              bindValue={bindExpedited}
            />
          </Grid>

          <Grid item xs={12} className="p-t-0">
            <TextAreaInput
              name="additionalComments"
              label="Additional comments (optional)"
              bindValue={bindAdditionalComments}
              rows={4}
            />
          </Grid>
        </Grid>

        {projectCreateContext ? (
          <div className="actions m-t-40">
            <Button
              variant="contained"
              color="primary"
              disabled={loadingUpdate}
              className={loadingUpdate ? 'loading' : ''}
              onClick={handleSaveAndExit}
            >
              Save and exit
            </Button>

            <div>
              <Button
                variant="outlined"
                color="primary"
                onClick={() =>
                  dispatch({
                    type: PROJECT_CURRENT_STEP,
                    payload: 6,
                  })
                }
                disabled={loadingUpdate}
                className={loadingUpdate ? 'loading' : ''}
              >
                Back
              </Button>
              <Button
                variant="contained"
                color="primary"
                style={{ marginLeft: 20 }}
                onClick={handleNext}
                disabled={loadingUpdate}
                className={loadingUpdate ? 'loading' : ''}
              >
                {loadingUpdate ? <Loader /> : 'Next'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="actions m-t-40 space-between flex">
            <Button
              variant="outlined"
              color="primary"
              onClick={handleCancel}
              disabled={loadingUpdate}
              className={loadingUpdate ? 'loading' : ''}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              style={{ marginLeft: 20 }}
              onClick={handleNext}
              disabled={loadingUpdate}
              className={loadingUpdate ? 'loading' : ''}
            >
              {loadingUpdate ? <Loader /> : 'Save'}
            </Button>
          </div>
        )}
      </FormContainer>
      <ModalPopUp
        title={
          selectedAddress && Object.keys(selectedAddress).length === 0
            ? 'Add new address'
            : 'Edit address'
        }
        setShowModal={setShowAddressModal}
        showModal={showAddressModal}
      >
        <FormContainer>
          <h2 className="m-b-30">
            {selectedAddress && Object.keys(selectedAddress).length === 0
              ? userInfo.isAdmin
                ? `Add new address for ${project.userName}`
                : 'Add new address'
              : userInfo.isAdmin
              ? `Edit address for ${project.userName}`
              : 'Edit address'}
          </h2>
          <AddEditAddress
            selectedAddress={selectedAddress}
            setShowForm={setShowAddressModal}
          />
        </FormContainer>
      </ModalPopUp>
      {selectedAddress && (
        <ModalPopUp
          title="Delete address?"
          setShowModal={handleDeleteClose}
          showModal={showDeleteModal}
        >
          <h2 className="m-b-30">
            Are you sure you want to remove this address?
          </h2>

          <div>
            {selectedAddress.companyName && (
              <>
                {selectedAddress.companyName} <br />
              </>
            )}
            {selectedAddress.streetAddress}
            {selectedAddress.aptSuiteOther !== '' && ' '}
            {selectedAddress.aptSuiteOther}
            <br />
            {selectedAddress.city}, {selectedAddress.state}{' '}
            {selectedAddress.zipCode}
          </div>

          <div className="actions space-between flex m-t-40">
            <Button
              variant="outlined"
              color="primary"
              onClick={handleDeleteClose}
              disabled={loadingDelete}
              className={loadingDelete ? 'loading' : ''}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => deleteHandler(selectedAddress._id)}
              disabled={loadingDelete}
              className={loadingDelete ? 'loading' : ''}
            >
              Delete
            </Button>
          </div>
        </ModalPopUp>
      )}
    </div>
  )
}

export default DeliveryDetails
